import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_ENDPOINTS } from "../config/api";
import { fetchLatestShorts } from "../config/youtube";
import {
  FaClock,
  FaMapMarkerAlt,
  FaTimes,
  FaPlaceOfWorship,
  FaPlay,
  FaArrowRight,
  FaChevronLeft,
  FaChevronRight
} from "react-icons/fa";

import "../styles/Home.css";
import { useHomam } from "../context/HomamContext";
import HomamFlashTicker from "../components/HomamFlashTicker";
import { getHomamExternalUrl } from "../utils/domain";
import { resolveImageUrl, getPoojaBackground } from "./Poojas";

/* ================= HIGHLIGHT IMAGE HELPERS ================= */
const getHighlightCardImage = (item, i, poojasList = []) => {
  if (item?.image) return resolveImageUrl(item.image);
  if (item?.images && Array.isArray(item.images) && item.images.length > 0 && item.images[0]) {
    return resolveImageUrl(item.images[0]);
  }

  const title = (item?.title || "").toLowerCase();
  const matchedPooja = (poojasList || []).find((p) => {
    const pName = (p?.name || "").toLowerCase();
    if (!pName) return false;
    if (title.includes(pName) || pName.includes(title)) return true;
    if (title.includes("kalasam") && pName.includes("kalasam")) return true;
    if (title.includes("gomata") && pName.includes("gomata")) return true;
    if (title.includes("dhuni") && pName.includes("dhuni")) return true;
    if (title.includes("santhana") && pName.includes("santhana")) return true;
    if ((title.includes("gho") || title.includes("ghoshala")) && (pName.includes("gho") || pName.includes("ghoshala"))) return true;
    if (title.includes("amavasya") && pName.includes("amavasya")) return true;
    if ((title.includes("sathya") || title.includes("satya")) && (pName.includes("sathya") || pName.includes("satya"))) return true;
    if (title.includes("murugan") && pName.includes("murugan")) return true;
    return false;
  });

  if (matchedPooja?.images && Array.isArray(matchedPooja.images) && matchedPooja.images.length > 0) {
    const firstValid = matchedPooja.images.find((img) => typeof img === "string" && img.trim() !== "");
    if (firstValid) {
      return resolveImageUrl(firstValid);
    }
  }

  const fallback = getPoojaBackground(item?.title || "");
  if (fallback) return fallback;

  const indexFallbacks = [
    '/images/kalasam_bg.jpg',
    '/images/gomata_darshan_bg.jpg',
    '/images/dhuni_pooja_bg.jpg',
    '/images/santhana_bhagya_bg.jpg',
    '/images/gho-pooja.jpg',
    '/images/amavasya_rituals_bg.jpg',
    '/images/sathya_narayana_bg.jpg',
    '/images/lord_murugan_bg.jpg'
  ];
  return indexFallbacks[i] || null;
};

const HighlightCard = ({ item, i, activeHighlight, toggleHighlight, poojas }) => {
  const primaryBg = getHighlightCardImage(item, i, poojas);
  const fallbackBg = getPoojaBackground(item?.title || "");
  const [bgSrc, setBgSrc] = useState(primaryBg);

  useEffect(() => {
    setBgSrc(getHighlightCardImage(item, i, poojas));
  }, [item, poojas, i]);

  const handleImgError = () => {
    if (fallbackBg && bgSrc !== fallbackBg) {
      setBgSrc(fallbackBg);
    }
  };

  return (
    <div
      key={i}
      className={`highlight-card ${activeHighlight === i ? "active" : ""}`}
      onClick={() => toggleHighlight(i)}
      style={bgSrc ? {
        backgroundImage: `url("${bgSrc}")`,
        backgroundSize: 'cover',
        backgroundPosition: i === 1 ? 'center top' : 'center',
        backgroundRepeat: 'no-repeat',
        position: 'relative',
        overflow: 'hidden',
      } : {}}
    >
      {bgSrc && fallbackBg && bgSrc !== fallbackBg && (
        <img
          src={bgSrc}
          alt=""
          style={{ display: "none" }}
          onError={handleImgError}
        />
      )}

      {bgSrc && (
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.68) 100%)',
          borderRadius: 'inherit',
          zIndex: 0,
          pointerEvents: 'none',
        }} />
      )}
      <h3 style={bgSrc ? {
        position: 'relative',
        zIndex: 1,
        color: '#ffffff',
        textShadow: '0 2px 8px rgba(0,0,0,0.85), 0 1px 3px rgba(0,0,0,0.9)',
        fontWeight: 700,
      } : {}}>{item.title}</h3>

      <p style={bgSrc ? {
        position: 'relative',
        zIndex: 1,
        color: '#ffffff',
        textShadow: '0 1px 6px rgba(0,0,0,0.9), 0 2px 4px rgba(0,0,0,0.85)',
        fontWeight: 500,
      } : {}}>
        {activeHighlight === i
          ? item.desc
          : "Click to explore divine significance"}
      </p>
    </div>
  );
};

/* ================= COMPONENT ================= */

const Home = () => {
  const navigate = useNavigate();
  const { isHomamActive } = useHomam();
  const reelFeedRef = useRef(null);

  /* ================= BACKEND DATA ================= */
  const [homeData, setHomeData] = useState(null);
  const [poojas, setPoojas] = useState([]);
  const [shorts, setShorts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [activeHighlight, setActiveHighlight] = useState(null);
  const [activeModalShort, setActiveModalShort] = useState(null);
  const [activeModalIndex, setActiveModalIndex] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  /* Auto switch carousel slide (3 seconds) only when Homam is active */
  useEffect(() => {
    if (!isHomamActive) {
      setCurrentSlide(0);
      return;
    }
    const slideTimer = setInterval(() => {
      setCurrentSlide((prev) => (prev === 0 ? 1 : 0));
    }, 3000);
    return () => clearInterval(slideTimer);
  }, [isHomamActive]);

  const handleNextSlide = (e) => {
    e?.stopPropagation?.();
    setCurrentSlide((prev) => (prev === 0 ? 1 : 0));
  };

  const handlePrevSlide = (e) => {
    e?.stopPropagation?.();
    setCurrentSlide((prev) => (prev === 0 ? 1 : 0));
  };

  /* ================= KEYBOARD & NATIVE VERTICAL REEL SCROLL ================= */
  useEffect(() => {
    if (activeModalIndex !== null && reelFeedRef.current) {
      setTimeout(() => {
        if (reelFeedRef.current) {
          const cardHeight = reelFeedRef.current.clientHeight || 580;
          reelFeedRef.current.scrollTop = activeModalIndex * cardHeight;
        }
      }, 50);
    }
  }, [activeModalIndex]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (activeModalIndex === null) return;
      if (e.key === "ArrowDown" || e.key === "PageDown") {
        e.preventDefault();
        const nextIdx = (activeModalIndex + 1) % shorts.length;
        setActiveModalIndex(nextIdx);
        if (reelFeedRef.current) {
          reelFeedRef.current.scrollTo({
            top: nextIdx * reelFeedRef.current.clientHeight,
            behavior: "smooth"
          });
        }
      } else if (e.key === "ArrowUp" || e.key === "PageUp") {
        e.preventDefault();
        const prevIdx = (activeModalIndex - 1 + shorts.length) % shorts.length;
        setActiveModalIndex(prevIdx);
        if (reelFeedRef.current) {
          reelFeedRef.current.scrollTo({
            top: prevIdx * reelFeedRef.current.clientHeight,
            behavior: "smooth"
          });
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeModalIndex, shorts.length]);

  /* ================= FETCH FROM BACKEND & YOUTUBE ================= */
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [homeRes, poojasRes] = await Promise.all([
          axios.get(API_ENDPOINTS.HOME),
          axios.get(API_ENDPOINTS.POOJAS).catch(() => ({ data: [] }))
        ]);

        /* IMPORTANT: your backend returns { message, data } */
        setHomeData(homeRes.data.data || homeRes.data);
        setPoojas(poojasRes.data || []);

        setLoading(false);
      } catch (err) {
        console.log(err);
        setLoading(false);
      }
    };

    const loadShorts = async () => {
      try {
        const data = await fetchLatestShorts(10);
        setShorts(data);
      } catch (err) {
        console.error("Shorts fetch error:", err);
      }
    };

    fetchData();
    loadShorts();
  }, []);

  const getYoutubeId = (url) => {
    if (!url) return "";
    if (url.includes("youtu.be/")) {
      return url.split("youtu.be/")[1].split("?")[0];
    }
    if (url.includes("watch?v=")) {
      return url.split("watch?v=")[1].split("&")[0];
    }
    if (url.includes("embed/")) {
      return url.split("embed/")[1].split("?")[0];
    }
    return url;
  };

  /* ================= ESC KEY MODAL ================= */
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") setShowModal(false);
    };

    window.addEventListener("keydown", handleEsc);

    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  const toggleHighlight = (index) => {
    setActiveHighlight((prev) => (prev === index ? null : index));
  };

  if (loading) return <h2>Loading...</h2>;

  return (
    <div className="home-page">

      {/* ⚡ Breaking / Flash Ticker (Only displayed when Nakshatra Homam is Active) */}
      {isHomamActive && <HomamFlashTicker />}

      {/* ================= HERO CAROUSEL ================= */}
      <section className="hero-section banner-hero">
        {/* Slide 1: Main Temple Overview */}
        <div className={`hero-slide slide-temple ${currentSlide === 0 ? "active" : ""}`}>
          <img
            src={homeData?.heroImage || "/images/banner.jpg"}
            alt="Sri Gnana Sai Baba Mandir Banner"
            className="hero-banner-image"
          />

          <div className="hero-overlay-content">
            <h1>{homeData?.heroTitle || "SRI GNANA SAI BABA MANDIR"}</h1>
            <p>{homeData?.heroSubtitle || "A Sacred Sanctum of Peace, Devotion, and Miracles"}</p>

            <div className="hero-buttons">
              <button
                className="btn primary-btn"
                onClick={() => setShowModal(true)}
              >
                Visit Temple
              </button>

              <button
                className="btn secondary-btn"
                onClick={() =>
                  document
                    .getElementById("features")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
              >
                View Poojas
              </button>
            </div>
          </div>
        </div>

        {/* Slide 2: Sai Sahasranama Ayushya Homam (Only displayed when isHomamActive is true) */}
        {isHomamActive && (
          <div
            className={`hero-slide slide-homam ${currentSlide === 1 ? "active" : ""}`}
            onClick={() => {
              const isProd = typeof window !== "undefined" && window.location.hostname.includes("srignanasaibabamandir.com");
              if (isProd) {
                window.location.href = getHomamExternalUrl("/#find-star");
              } else {
                navigate("/nakshatra-homam#find-star");
              }
            }}
            title="Click to view 27 Nakshatra Homam Schedule & Booking"
          >
            <img
              src="/images/nakshatra_homam_home_banner.png"
              alt="Sri Gnana Sai Baba Mandir - Sai Sahasranama Ayushya Homam"
              className="hero-banner-image"
              onError={(e) => {
                e.target.src = "/images/nakshatra_homam_home_banner.png";
              }}
            />

            <div
              className="homam-slide-interactive-badge"
              onClick={(e) => {
                e.stopPropagation();
                const isProd = typeof window !== "undefined" && window.location.hostname.includes("srignanasaibabamandir.com");
                if (isProd) {
                  window.location.href = getHomamExternalUrl("/#find-star");
                } else {
                  navigate("/nakshatra-homam#find-star");
                }
              }}
            >
              <span>Explore 27 Days Homam Schedule & Book</span>
              <FaArrowRight className="badge-arrow-icon" style={{ fontSize: 13 }} />
            </div>
          </div>
        )}

        {/* Carousel Navigation Arrows (Only when 2 slides exist) */}
        {isHomamActive && (
          <>
            <button
              type="button"
              className="hero-carousel-arrow prev"
              onClick={handlePrevSlide}
              aria-label="Previous Slide"
            >
              <FaChevronLeft style={{ fontSize: 15 }} />
            </button>

            <button
              type="button"
              className="hero-carousel-arrow next"
              onClick={handleNextSlide}
              aria-label="Next Slide"
            >
              <FaChevronRight style={{ fontSize: 15 }} />
            </button>

            {/* Carousel Indicator Dots */}
            <div className="hero-carousel-dots">
              <span
                className={`hero-carousel-dot ${currentSlide === 0 ? "active" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentSlide(0);
                }}
                title="Temple Overview"
              />
              <span
                className={`hero-carousel-dot ${currentSlide === 1 ? "active" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentSlide(1);
                }}
                title="27 Nakshatra Maha Homam"
              />
            </div>
          </>
        )}
      </section>

      {/* ================= HIGHLIGHTS ================= */}
      <section className="temple-highlight">
        <div className="section-title">
          <h2>TEMPLE HIGHLIGHTS</h2>
        </div>

        <div className="highlight-grid">
          {(homeData?.highlights || []).map((item, i) => (
            <HighlightCard
              key={i}
              item={item}
              i={i}
              activeHighlight={activeHighlight}
              toggleHighlight={toggleHighlight}
              poojas={poojas}
            />
          ))}
        </div>

        <div className="highlight-footer">
          <button
            className="explore-btn"
            onClick={() => navigate("/poojas")}
          >
            Explore More
          </button>
        </div>
      </section>

      {/* ================= FEATURES ================= */}
      <section className="features-section" id="features">
        <div className="section-title">
          <h2>SPECIAL FEATURES</h2>
        </div>

        <div className="features-grid">

          <div className="feature-card" onClick={() => navigate("/timings")}>
            <h3>Daily Pooja Timings</h3>
            <button onClick={(e) => { e.stopPropagation(); navigate("/timings"); }}>
              View
            </button>
          </div>

          <div className="feature-card" onClick={() => navigate("/live-darshan")}>
            <h3>Live Darshan</h3>
            <button onClick={(e) => { e.stopPropagation(); navigate("/live-darshan"); }}>
              Watch
            </button>
          </div>

          <div className="feature-card" onClick={() => navigate("/festivals")}>
            <h3>Prasad Distribution</h3>
            <button onClick={(e) => { e.stopPropagation(); navigate("/festivals"); }}>
              View
            </button>
          </div>

          <div className="feature-card" onClick={() => navigate("/poojas")}>
            <h3>Temple Events</h3>
            <button onClick={(e) => { e.stopPropagation(); navigate("/poojas"); }}>
              Explore
            </button>
          </div>

          <div className="feature-card" onClick={() => navigate("/gallery")}>
            <h3>Photo Gallery</h3>
            <button onClick={(e) => { e.stopPropagation(); navigate("/gallery"); }}>
              Open
            </button>
          </div>

          <div className="feature-card" onClick={() => navigate("/about")}>
            <h3>Temple History</h3>
            <button onClick={(e) => { e.stopPropagation(); navigate("/about"); }}>
              Read
            </button>
          </div>

        </div>
      </section>

      {/* ================= EVENTS ================= */}
      <section className="events-section">
        <div className="section-title">
          <h2>LATEST EVENTS & UPDATES</h2>
        </div>

        <div className="events-container">
          {(homeData?.events || []).map((event, index) => (
            <div
              key={index}
              className={`event-row ${index % 2 ? "reverse" : ""
                }`}
            >
              <div className="event-text">
                <h3>{event.title}</h3>
                <p>{event.desc}</p>
              </div>

              <div
                className="event-video"
                onClick={() =>
                  setActiveModalShort({
                    title: event.title,
                    embedUrl: `https://www.youtube.com/embed/${getYoutubeId(event.embed || event.youtube)}`
                  })
                }
              >
                <img
                  src={`https://i.ytimg.com/vi/${getYoutubeId(event.embed || event.youtube)}/hqdefault.jpg`}
                  alt={event.title}
                  className="event-thumb-img"
                  loading="lazy"
                  decoding="async"
                  onError={(e) => {
                    e.target.src = "/images/banner.jpg";
                  }}
                />

                <div className="event-play-overlay">
                  <div className="event-play-circle">
                    <FaPlay className="play-icon" />
                  </div>
                  <span>Watch Event Video</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ================= DIVINE SHORTS CAROUSEL (POSITION 1) ================= */}
      <section className="newsghuru-shorts-section">
        <div className="section-title shorts-section-title">
          <h2>DIVINE SHORTS</h2>
          <button
            className="view-all-shorts-btn"
            onClick={() => navigate("/shorts")}
          >
            View All Shorts <FaArrowRight />
          </button>
        </div>

        <div className="shorts-horizontal-track">
          {shorts.map((short, idx) => (
            <div
              className="newsghuru-reel-card"
              key={short.id || idx}
              onClick={() => setActiveModalIndex(idx)}
            >
              {/* TOP BADGE */}
              <div className="reel-badge-pill">{short.badge || "🪔 DIVINE"}</div>

              {/* VERTICAL HIGH-RES THUMBNAIL */}
              <div className="reel-thumb-box">
                <img
                  src={short.thumbnailUrl || `https://i.ytimg.com/vi/${short.videoId}/hqdefault.jpg`}
                  alt={short.title}
                  className="reel-thumb-img"
                  loading="lazy"
                  decoding="async"
                  onError={(e) => {
                    e.target.src = "/images/banner.jpg";
                  }}
                />
              </div>

              {/* OVERLAY */}
              <div className="reel-overlay-gradient">
                <span className="play-tag-text">
                  <FaPlay className="play-icon" /> Play Reel
                </span>
                <h4 className="reel-title-text">{short.title}</h4>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FULLSCREEN REEL MODAL PLAYER */}
      {activeModalIndex !== null && shorts[activeModalIndex] && (
        <div
          className="shorts-modal-overlay"
          onClick={() => setActiveModalIndex(null)}
        >
          <div
            className="shorts-modal-card"
            onClick={(e) => e.stopPropagation()}
          >
            {/* FLOATING CLOSE BUTTON DIRECTLY OVER VIDEO */}
            <button
              className="shorts-modal-close floating-close"
              onClick={() => setActiveModalIndex(null)}
              title="Close"
            >
              ✕
            </button>

            {/* NATIVE VERTICAL SNAP SCROLL FEED */}
            <div
              className="modal-vertical-snap-container"
              ref={reelFeedRef}
              onScroll={(e) => {
                const target = e.target;
                const index = Math.round(target.scrollTop / target.clientHeight);
                if (index !== activeModalIndex && index >= 0 && index < shorts.length) {
                  setActiveModalIndex(index);
                }
              }}
            >
              {shorts.map((item, idx) => (
                <div
                  className="shorts-snap-card"
                  key={item.id || idx}
                >
                  {idx === activeModalIndex ? (
                    <iframe
                      src={`https://www.youtube-nocookie.com/embed/${item.videoId || (item.embedUrl?.split("embed/")[1]?.split("?")[0])}?autoplay=1&mute=0&controls=1&rel=0&playsinline=1&enablejsapi=1`}
                      title={item.title || `Sri Gnana Sai Baba Mandir Short Reel ${idx + 1}`}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allowFullScreen
                    />
                  ) : (
                    <img
                      src={item.thumbnailUrl || `https://i.ytimg.com/vi/${item.videoId}/hqdefault.jpg`}
                      alt={item.title}
                      className="snap-card-thumb"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* EVENT VIDEO MODAL */}
      {activeModalShort && (
        <div
          className="shorts-modal-overlay"
          onClick={() => setActiveModalShort(null)}
        >
          <div
            className="shorts-modal-card"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="shorts-modal-close floating-close"
              onClick={() => setActiveModalShort(null)}
              title="Close"
            >
              ✕
            </button>
            <div className="modal-vertical-snap-container">
              <iframe
                src={`${activeModalShort.embedUrl}?autoplay=1&mute=0&controls=1`}
                title={activeModalShort.title || "Event Video"}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}

      {/* ================= DIVINE ANNADANAM ================= */}
      <section className="annadanam-section">
        <div className="section-title">
          <h2>DIVINE ANNADANAM</h2>
        </div>

        <div className="annadanam-grid">
          {(homeData?.annadanam || []).map((item, i) => (
            <div key={i} className="annadanam-card">

              <h3>{item.title}</h3>

              <p>{item.desc}</p>

            </div>
          ))}
        </div>
      </section>

      {/* ================= GALLERY ================= */}
      <section className="gallery-section">
        <div className="section-title">
          <h2>TEMPLE GALLERY</h2>
        </div>

        <div className="gallery-grid">
          {(homeData?.gallery || []).map((img, index) => (
            <div key={index} className="gallery-item">
              <img
                src={img}
                alt={`Gallery ${index}`}
                loading="lazy"
                decoding="async"
              />
            </div>
          ))}
        </div>

        <div className="gallery-footer">
          <button
            className="explore-btn"
            onClick={() => navigate("/gallery")}
          >
            Explore More Gallery
          </button>
        </div>
      </section>

      {/* ================= MODAL ================= */}
      {showModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowModal(false)}
        >
          <div
            className="modal-box premium-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>
                <FaPlaceOfWorship />
                {" "}
                Temple Information
              </h2>

              <button
                className="close-btn"
                onClick={() => setShowModal(false)}
              >
                <FaTimes />
              </button>
            </div>

            <div className="modal-body">

              <div className="info-card">
                <FaClock />

                <div>
                  <h4>Darshan Timing</h4>
                  <p>6:00 AM – 9:00 PM</p>
                </div>
              </div>

              <div className="info-card">
                <FaMapMarkerAlt />

                <div>
                  <h4>Location</h4>
                  <p>Ambattur, Chennai</p>
                </div>
              </div>

              <p className="modal-quote">
                “Faith brings peace, devotion brings grace ✨”
              </p>

            </div>

            <div className="modal-footer">
              <button
                className="modal-btn"
                onClick={() => {
                  setShowModal(false);
                  navigate("/live-darshan");
                }}
              >
                Start Live Darshan
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default Home;